import React, { useMemo } from 'react';
import { FileText, Calendar, TrendingUp, Eye, Download, CheckCircle, Clock } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { BankStatement } from '../../types/bank-reconciliation';

interface RecentStatementsListProps {
  statements: BankStatement[];
  onSelectStatement?: (statement: BankStatement) => void;
  className?: string;
}

export function RecentStatementsList({
  statements,
  onSelectStatement,
  className
}: RecentStatementsListProps) {
  // Get bank name from code
  const getBankName = (bankCode: string) => {
    const bankMap: { [key: string]: string } = {
      '001': 'Banco do Brasil',
      '033': 'Santander',
      '104': 'Caixa',
      '237': 'Bradesco',
      '341': 'Itaú',
      '260': 'Nubank',
      '077': 'Inter',
      '756': 'Sicoob',
      '748': 'Sicredi',
      '208': 'BTG Pactual'
    };
    return bankMap[bankCode] || bankCode;
  };

  // Format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(amount);
  };

  // Format date
  const formatDate = (date: string) => {
    if (!date) return 'Data não disponível';

    try {
      const dateObj = new Date(date);
      // Check if the date is valid
      if (isNaN(dateObj.getTime())) {
        return 'Data inválida';
      }

      return new Intl.DateTimeFormat('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      }).format(dateObj);
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Data inválida';
    }
  };

  // Format relative time
  const getRelativeTime = (date: string) => {
    if (!date) return 'Data não disponível';

    try {
      const now = new Date();
      const statementDate = new Date(date);

      // Check if the date is valid
      if (isNaN(statementDate.getTime())) {
        return 'Data inválida';
      }

      const diffMs = now.getTime() - statementDate.getTime();
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

      if (diffDays === 0) return 'Hoje';
      if (diffDays === 1) return 'Ontem';
      if (diffDays < 7) return `Há ${diffDays} dias`;
      if (diffDays < 30) return `Há ${Math.floor(diffDays / 7)} semanas`;
      if (diffDays < 365) return `Há ${Math.floor(diffDays / 30)} meses`;
      return `Há ${Math.floor(diffDays / 365)} anos`;
    } catch (error) {
      console.error('Error calculating relative time:', error);
      return 'Data inválida';
    }
  };

  // Calculate statistics
  const stats = useMemo(() => {
    const totalStatements = statements.length;
    const completedStatements = statements.filter(s => s.status === 'completed').length;
    const recentStatements = statements.filter(s => {
      const diffMs = Date.now() - new Date(s.created_at).getTime();
      return diffMs < 7 * 24 * 60 * 60 * 1000; // Last 7 days
    }).length;

    return {
      total: totalStatements,
      completed: completedStatements,
      recent: recentStatements,
      completionRate: totalStatements > 0 ? (completedStatements / totalStatements) * 100 : 0
    };
  }, [statements]);

  // Get status badge props
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
        return { variant: 'default' as const, className: 'bg-green-100 text-green-800 border-green-200', icon: CheckCircle, text: 'Concluído' };
      case 'processing':
        return { variant: 'outline' as const, className: 'bg-yellow-100 text-yellow-800 border-yellow-200', icon: Clock, text: 'Processando' };
      case 'pending':
        return { variant: 'outline' as const, className: 'bg-blue-100 text-blue-800 border-blue-200', icon: Clock, text: 'Pendente' };
      default:
        return { variant: 'outline' as const, className: 'bg-card text-gray-800 border-border', icon: Clock, text: status };
    }
  };

  if (statements.length === 0) {
    return (
      <Card className={className}>
        <CardContent className="p-8 text-center">
          <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-medium mb-2">Nenhum extrato importado</h3>
          <p className="text-muted-foreground">
            Importe seu primeiro arquivo .ofx para começar a conciliação.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className={className}>
      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total</p>
                <p className="text-2xl font-bold">{stats.total}</p>
              </div>
              <FileText className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Concluídos</p>
                <p className="text-2xl font-bold text-green-600">{stats.completed}</p>
              </div>
              <CheckCircle className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Recentes</p>
                <p className="text-2xl font-bold text-blue-600">{stats.recent}</p>
              </div>
              <Calendar className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Taxa</p>
                <p className="text-2xl font-bold">{stats.completionRate.toFixed(0)}%</p>
              </div>
              <TrendingUp className="h-8 w-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Statements List */}
      <div className="space-y-3">
        {statements.map((statement) => {
          const statusBadge = getStatusBadge(statement.status);
          const StatusIcon = statusBadge.icon;

          return (
            <Card key={statement.id} className="hover:shadow-md transition-shadow">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    {/* Bank Info */}
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-medium">{getBankName(statement.bank_code)}</h4>
                        <Badge
                          variant={statusBadge.variant}
                          className={statusBadge.className}
                        >
                          <StatusIcon className="h-3 w-3 mr-1" />
                          {statusBadge.text}
                        </Badge>
                      </div>

                      <div className="flex items-center gap-4 text-sm text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {formatDate(statement.start_date)} - {formatDate(statement.end_date)}
                        </span>

                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {getRelativeTime(statement.created_at)}
                        </span>

                        <span>
                          Conta: {statement.account_number || 'N/A'}
                        </span>
                      </div>

                      {/* Financial Summary */}
                      <div className="flex items-center gap-4 mt-2">
                        <span className="text-sm font-medium text-green-600">
                          Entradas: {formatCurrency(statement.total_credit || 0)}
                        </span>
                        <span className="text-sm font-medium text-red-600">
                          Saídas: {formatCurrency(statement.total_debit || 0)}
                        </span>
                        <span className="text-sm text-muted-foreground">
                          {statement.transaction_count || 0} transações
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2">
                    {statement.status === 'completed' && onSelectStatement && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onSelectStatement(statement)}
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        Ver
                      </Button>
                    )}

                    <Button variant="outline" size="sm">
                      <Download className="h-4 w-4 mr-1" />
                      Exportar
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}